/**
 * File transfer protocol — mirrors `host-agent/core/src/proto.rs::FileMessage` for control
 * messages, plus a fixed 24-byte binary chunk header.
 *
 * Wire format on the `files` DataChannel:
 *   - TEXT frames:   JSON-encoded FileMessage
 *   - BINARY frames: [16 B UUID][8 B LE offset][≤ 32 KiB payload]
 *
 * Coexisting on a single SCTP stream is supported by WebRTC DataChannel — `event.data` is either
 * `string` or `ArrayBuffer` depending on the frame. We set `binaryType = 'arraybuffer'` on the
 * receiver so binary frames arrive as `ArrayBuffer`, never `Blob`.
 */

export type FileMessage =
  | { type: 'file_offer'; id: string; name: string; size: number; sha256?: string }
  | { type: 'file_accept'; id: string }
  | { type: 'file_cancel'; id: string }
  | { type: 'file_complete'; id: string }
  | { type: 'file_progress'; id: string; bytes_received: number }
  | { type: 'file_done'; id: string }
  | { type: 'file_error'; id: string; error: string };

export const FILE_CHUNK_HEADER_LEN = 24;
export const FILE_CHUNK_PAYLOAD_LIMIT = 32 * 1024;

/** Pack a 24-byte chunk header. UUID must be canonical with hyphens; we strip them and parse. */
export function buildChunkHeader(transferId: string, offset: number): ArrayBuffer {
  const buf = new ArrayBuffer(FILE_CHUNK_HEADER_LEN);
  const view = new DataView(buf);
  const bytes = uuidToBytes(transferId);
  for (let i = 0; i < 16; i++) view.setUint8(i, bytes[i]);
  // Browsers don't have setBigUint64 on all targets; pack manually as little-endian.
  const lo = offset >>> 0;
  const hi = Math.floor(offset / 0x100000000) >>> 0;
  view.setUint32(16, lo, true);
  view.setUint32(20, hi, true);
  return buf;
}

function uuidToBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, '');
  if (hex.length !== 32) throw new Error(`bad uuid: ${uuid}`);
  const out = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

export function newTransferId(): string {
  // crypto.randomUUID is available in all modern browsers + secure contexts (HTTPS / localhost).
  return crypto.randomUUID();
}

export function sendFileControl(channel: RTCDataChannel | null, msg: FileMessage): boolean {
  if (!channel || channel.readyState !== 'open') return false;
  try {
    channel.send(JSON.stringify(msg));
    return true;
  } catch {
    return false;
  }
}
